import { CommonModule } from "@angular/common";
import { Component, effect, EventEmitter, inject, OnDestroy, OnInit, Output, signal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { merge, Subject, takeUntil } from "rxjs";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { BtAuthorizationDialogComponent } from "../bt-authorization-dialog/bt-authorization-dialog.component";
import { BtBankAccountInformationPayload } from "../../interfaces/bt-bank-account-payload";


@Component({
  selector: "equine-ui-nx-bt-ach-bank-account-dialog",
  templateUrl: "./bt-ach-bank-account-dialog.html",
  styleUrl: './bt-ach-bank-account-dialog.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule
  ]
})

export class BtAchBankAccountDialogComponent implements OnInit, OnDestroy {
  
  readonly dialog = inject(MatDialog);
  
  @Output() formSubmit = new EventEmitter<{ bankDetails: any; mandateText: string; }>();

  private fb = new FormBuilder();
  form!: FormGroup;
  private unsubscribe$ = new Subject<void>();
  authorizationControl!: FormControl;
  mandateText!: string | null;

  accountValues = [
    { label: 'Savings', value: "savings" },
    { label: 'Checking', value: "checking" }
  ];
  ownershipTypes = [
    { label: 'Personal', value: "personal" },
    { label: 'Business', value: "business" },
  ];

  states = [{"name":"Alabama","abbreviation":"AL"},{"name":"Alaska","abbreviation":"AK"},{"name":"Arizona","abbreviation":"AZ"},{"name":"Arkansas","abbreviation":"AR"},{"name":"California","abbreviation":"CA"},{"name":"Colorado","abbreviation":"CO"},{"name":"Connecticut","abbreviation":"CT"},{"name":"Delaware","abbreviation":"DE"},{"name":"Florida","abbreviation":"FL"},{"name":"Georgia","abbreviation":"GA"},{"name":"Hawaii","abbreviation":"HI"},{"name":"Idaho","abbreviation":"ID"},{"name":"Illinois","abbreviation":"IL"},{"name":"Indiana","abbreviation":"IN"},{"name":"Iowa","abbreviation":"IA"},{"name":"Kansas","abbreviation":"KS"},{"name":"Kentucky","abbreviation":"KY"},{"name":"Louisiana","abbreviation":"LA"},{"name":"Maine","abbreviation":"ME"},{"name":"Maryland","abbreviation":"MD"},{"name":"Massachusetts","abbreviation":"MA"},{"name":"Michigan","abbreviation":"MI"},{"name":"Minnesota","abbreviation":"MN"},{"name":"Mississippi","abbreviation":"MS"},{"name":"Missouri","abbreviation":"MO"},{"name":"Montana","abbreviation":"MT"},{"name":"Nebraska","abbreviation":"NE"},{"name":"Nevada","abbreviation":"NV"},{"name":"New Hampshire","abbreviation":"NH"},{"name":"New Jersey","abbreviation":"NJ"},{"name":"New Mexico","abbreviation":"NM"},{"name":"New York","abbreviation":"NY"},{"name":"North Carolina","abbreviation":"NC"},{"name":"North Dakota","abbreviation":"ND"},{"name":"Ohio","abbreviation":"OH"},{"name":"Oklahoma","abbreviation":"OK"},{"name":"Oregon","abbreviation":"OR"},{"name":"Pennsylvania","abbreviation":"PA"},{"name":"Rhode Island","abbreviation":"RI"},{"name":"South Carolina","abbreviation":"SC"},{"name":"South Dakota","abbreviation":"SD"},{"name":"Tennessee","abbreviation":"TN"},{"name":"Texas","abbreviation":"TX"},{"name":"Utah","abbreviation":"UT"},{"name":"Vermont","abbreviation":"VT"},{"name":"Virginia","abbreviation":"VA"},{"name":"Washington","abbreviation":"WA"},{"name":"West Virginia","abbreviation":"WV"},{"name":"Wisconsin","abbreviation":"WI"},{"name":"Wyoming","abbreviation":"WY"}];

  isLoading = signal(false);
  error = signal<string | null>(null);
  disableForm = effect(() => {
    if (this.isLoading()) {
      this.form.disable();
      this.authorizationControl.disable();
    } else {
      this.form.enable();
      this.authorizationControl.enable();
    }
  });

  constructor(
  ) { }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm() {
    this.form = this.fb.group({
      accountType: ['', Validators.required],
      ownershipType: ['', Validators.required],
      routingNumber: ['', Validators.compose([Validators.required, Validators.pattern('[0-9]{8,9}')])],
      accountNumber: ['', Validators.compose([Validators.required, Validators.pattern('[0-9]{7,18}')])],
      businessName: [''],
      firstName: [''],
      lastName: [''],

      billingAddress: this.fb.group({
        streetAddress: ['', Validators.required],
        extendedAddress: [''],
        locality: ['', Validators.required],
        region: ['', Validators.required],
        postalCode: ['', Validators.compose([Validators.required, Validators.pattern('[0-9]{5}(?:-[0-9]{4})?')])]
      }),
    });

    this.authorizationControl = this.fb.control(false, Validators.requiredTrue)

    this.subscribeToFormLogic();
  }

  subscribeToFormLogic() {
    this.form.get('ownershipType')?.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (value) => {
          if (value === 'personal') {
            this.form.controls['firstName'].setValidators([Validators.required]);
            this.form.controls['lastName'].setValidators([Validators.required]);
            this.form.controls['businessName'].clearValidators();
            this.form.controls['businessName'].reset();
          }
          if (value === 'business') {
            this.form.controls['firstName'].clearValidators();
            this.form.controls['firstName'].reset();
            this.form.controls['lastName'].clearValidators();
            this.form.controls['lastName'].reset();
            this.form.controls['businessName'].setValidators([Validators.required]);
          }
        }
      });

    
    merge([
      this.form.get('accountNumber')?.valueChanges,
      this.form.get('routingNumber')?.valueChanges
    ])
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: () => {
          this.setMandateText();
        }
      });

    this.authorizationControl.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe({
        next: (authControlValue) => {
          if (authControlValue === true) {
            this.setMandateText();
          }
        }
      });
  }

  openAuthorizationDialog() {
    this.setMandateText();
    
    this.dialog.open(BtAuthorizationDialogComponent, {
      data: this.mandateText
    }).afterClosed().subscribe({
      next: (approved) => {
        if (approved) {
          this.authorizationControl.setValue(true);
        } else {
          this.authorizationControl.setValue(false);
          this.mandateText = null;
        }
      }
    });
  }

  setMandateText() {
    let depositoryName;
    const theForm = this.form.value;
    if (theForm.ownershipType === 'personal') {
      depositoryName = theForm.firstName + ' ' + theForm.lastName;
    }
    if (theForm.ownershipType === 'business') {
      depositoryName = theForm.businessName;
    }

    this.mandateText = `By clicking ["Checkout"], I authorize Braintree, a service of PayPal, on behalf of Innately on behalf of IEA to verify my bank account information using bank information and consumer reports and I authorize Innalety to initiate an ACH/electronic debit to my checking/savings account, Depository Name: ${depositoryName}, Routing Number: ${theForm.routingNumber} and Account Number: ${theForm.accountNumber},
    that will be stored on file, and debited on or after the due date.
    I agree the ACH transactions I authorize comply with all applicable laws.
    I understand that this authorization will remain in full force and effect until I notify Innately that
    I wish to revoke this authorization.`;
  }

  get ownershipType(): string | undefined {
    return this.form?.get('ownershipType')?.value;
  }

  registerAccount() {
    const payload: { bankDetails: BtBankAccountInformationPayload, mandateText: string } = {
      bankDetails: this.cleanForm(),
      mandateText: this.mandateText as string
    }
    this.formSubmit.emit(payload);
  }

  cleanForm() {
    const theForm = this.form.value;

    if (theForm.ownershipType === 'personal') {
      delete theForm.business;
    }
    if (theForm.ownershipType === 'business') {
      delete theForm.firstName;
      delete theForm.lastName;
    }

    return theForm;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}